/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';
import 'client-only';

import { clsx } from '@accelint/design-foundation/lib/utils';
import { useCardInteractions } from '../../hooks/kanban';
import { Divider } from '../divider';
import { useDragContext } from './kanban';
import styles from './styles.module.css';
import type { KanbanCardProps } from './types';

/**
 * KanbanCard - Individual draggable card component
 *
 * Provides drag-and-drop functionality with visual feedback
 * including drop position indicators.
 *
 * @param props - {@link KanbanCardProps}
 * @param props.className - Optional CSS class name.
 * @param props.children - Card content (header, body).
 * @param props.card - Card data.
 * @param props.isActive - Whether this card is currently active/selected.
 * @returns The rendered KanbanCard component.
 *
 * @example
 * ```tsx
 * <KanbanCard card={cardData}>
 *   <KanbanCardHeader>
 *     <KanbanCardHeaderTitle>{card.title}</KanbanCardHeaderTitle>
 *   </KanbanCardHeader>
 *   <KanbanCardBody>{card.body}</KanbanCardBody>
 * </KanbanCard>
 * ```
 */
export function KanbanCard({
  className,
  children,
  card,
  isActive,
  ...rest
}: KanbanCardProps) {
  const { isDragging, closestEdge, ref, style, attributes, listeners } =
    useCardInteractions(card);
  const { activeId } = useDragContext();

  // Check if we should show the drop placeholder
  const showPlaceholder = activeId && activeId !== card.id && closestEdge;

  return (
    <li className={styles.cardContainerOuter} ref={ref} style={style}>
      {showPlaceholder && closestEdge === 'top' && (
        <Divider className={styles.divider} />
      )}

      <div
        data-dragging={isDragging || undefined}
        data-current={isActive || undefined}
        className={clsx(styles.card, className)}
        {...attributes}
        {...listeners}
        {...rest}
      >
        {children}
      </div>

      {showPlaceholder && closestEdge === 'bottom' && (
        <Divider className={styles.divider} />
      )}
    </li>
  );
}
